import { useState, useEffect } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import GenericEditModal from '../../components/modal/GenericEditModal';

const AllPage = () => {
  const [tableNames, setTableNames] = useState<string[]>([]);
  const [data, setData] = useState<{ [key: string]: any[] }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTableName, setCurrentTableName] = useState('');
  const [currentRowData, setCurrentRowData] = useState<any>(null);
  const [activeTable, setActiveTable] = useState<string>('');
  const [isNavOpen, setIsNavOpen] = useState(false);

  const fetchTableNames = async () => {
    try {
      const response = await api.get('/api/all/tables', {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const fetchedTableNames = response.data;
      setTableNames(fetchedTableNames);

      const allTableData: { [key: string]: any[] } = {};
      for (const tableName of fetchedTableNames) {
        try {
          const tableResponse = await api.get(`/api/all/${tableName}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
          });
          allTableData[tableName] = tableResponse.data;
        } catch (tableError) {
          console.error(`Failed to fetch data for table ${tableName}`, tableError);
          allTableData[tableName] = [];
        }
      }
      setData(allTableData);

      if (fetchedTableNames.length > 0) {
        setActiveTable(fetchedTableNames[0]);
      }
    } catch (error) {
      console.error('Failed to fetch table names', error);
    }
  };

  const fetchDataForTable = async (tableName: string) => {
    try {
      const response = await api.get(`/api/all/${tableName}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      setData((prevData) => ({ ...prevData, [tableName]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch data for table ${tableName}`, error);
    }
  };

  useEffect(() => {
    fetchTableNames();
  }, []);

  useEffect(() => {
    if (activeTable) {
      fetchDataForTable(activeTable);
    }
  }, [activeTable]);

  const handleCreate = async (tableName: string, newData: any) => {
    try {
      await api.post(`/api/all/${tableName}`, newData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      fetchDataForTable(tableName); // Refresh data
    } catch (error) {
      console.error(`Failed to create record in ${tableName}`, error);
    }
  };

  const handleUpdate = async (tableName: string, updatedData: any) => {
    try {
      await api.patch(`/api/all/${tableName}/${updatedData.id}`, updatedData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      fetchDataForTable(tableName); // Refresh data
    } catch (error) {
      console.error(`Failed to update record in ${tableName}`, error);
    }
  };

  const handleDelete = async (tableName: string, id: number) => {
    if (window.confirm(`Are you sure you want to delete record ${id} from ${tableName}?`)) {
      try {
        await api.delete(`/api/all/${tableName}/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        });
        fetchDataForTable(tableName); // Refresh data
      } catch (error) {
        console.error(`Failed to delete record from ${tableName}`, error);
      }
    }
  };

  const openCreateModal = (tableName: string) => {
    const tableColumns = data[tableName]?.length > 0 ? Object.keys(data[tableName][0]) : [];
    const defaultData = tableColumns.reduce((acc: { [key: string]: any }, key) => {
      acc[key] = '';
      return acc;
    }, {});
    setCurrentTableName(tableName);
    setCurrentRowData(defaultData);
    setIsModalOpen(true);
  };

  const openEditModal = (tableName: string, row: any) => {
    setCurrentTableName(tableName);
    setCurrentRowData(row);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setCurrentTableName('');
    setCurrentRowData(null);
  };

  const handleSave = (tableName: string, dataToSave: any) => {
    if (dataToSave.id) {
      handleUpdate(tableName, dataToSave);
    } else {
      handleCreate(tableName, dataToSave);
    }
  };

  const handleTableSelect = (tableName: string) => {
    setActiveTable(tableName);
    setIsNavOpen(false); // Close nav on selection
  };

  return (
    <div className="w-full h-full flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col md:flex-row w-full relative pt-6 px-4"
      >
        {/* Mobile Navigation - Dropdown */}
        <div className="md:hidden w-full mb-4">
          <button
            onClick={() => setIsNavOpen(!isNavOpen)}
            className="w-full bg-gray-800 text-white font-bold py-2 px-4 rounded-lg flex justify-between items-center"
          >
            <span>{activeTable || 'Select a Table'}</span>
            <svg className={`w-5 h-5 transform transition-transform ${isNavOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
          </button>
          {isNavOpen && (
            <ul className="bg-black bg-opacity-80 rounded-lg mt-2">
              {tableNames.map((tableName) => (
                <li key={tableName}>
                  <button
                    onClick={() => handleTableSelect(tableName)}
                    className={`w-full text-left py-2 px-4 transition duration-150 ease-in-out ${
                      activeTable === tableName ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'
                    }`}>
                    {tableName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Desktop Navigation - Sidebar */}
        <div className="hidden md:block w-full md:w-1/4 bg-black bg-opacity-60 p-4 rounded-lg border border-gray-800 shadow-lg">
          <h1 className="text-2xl font-bold mb-8 text-white">Tables</h1>
          <ul>
            {tableNames.map((tableName) => (
              <li key={tableName} className="mb-2">
                <button
                  onClick={() => setActiveTable(tableName)}
                  className={`w-full text-left py-2 px-4 rounded-lg transition duration-150 ease-in-out ${
                    activeTable === tableName ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'
                  }`}>
                  {tableName}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Table Content */}
        <div className="w-full md:w-3/4 bg-black bg-opacity-60 p-4 rounded-lg border border-gray-800 shadow-lg md:ml-4">
          {activeTable && data[activeTable] ? (
            <div>
              <h2 className="text-xl font-bold mb-2 text-white">{activeTable}</h2>
              <button
                onClick={() => openCreateModal(activeTable)} // Pass empty object for new record
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out mb-4"
              >
                Create New in {activeTable}
              </button>
              <div className="overflow-x-auto">
                <table className="table w-full">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      {data[activeTable].length > 0 && Object.keys(data[activeTable][0]).map((key) => (
                        <th key={key} className="py-3 px-4 text-left">{key}</th>
                      ))}
                      <th className="py-3 px-4 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-white">
                    {data[activeTable].map((row, index) => (
                      <tr key={row.id || index} className="border-b border-gray-700 hover:bg-gray-800">
                        {Object.values(row).map((value, i) => (
                          <td key={i} className="py-3 px-4">{String(value)}</td>
                        ))}
                        <td className="py-3 px-4 flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(activeTable, row)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(activeTable, row.id)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-white text-center py-10">Please select a table to view its content.</div>
          )}
        </div>
      </div>
      {isModalOpen && currentRowData && (
        <GenericEditModal
          isOpen={isModalOpen}
          onClose={closeEditModal}
          tableName={currentTableName}
          rowData={currentRowData}
          onSave={handleSave}
        />
      )}
      <AdminFooter />
    </div>
  );
};

export default AllPage;
